import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import {
  IRequest,
  IRequestWithSpecializationName,
} from 'legacy/lut/classes/models/onboarding/IRequest'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { ILastPartnerChange } from 'legacy/lut/classes/services/api/OnboardingApi'
import { getErrorResponse } from 'legacy/lut/functions'

import { WebSocketStoreKeys } from 'clickNgo/configs/service/ws'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { TResponse } from 'core/classes/ws/WebSocketClient'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'
import {
  IAggregateWorkflowErrorResponsePayload,
  IExpiredErrorResponsePayload,
  IInvalidChangePartnerErrorResponsePayload,
  IInvalidParamsErrorResponsePayload,
  IPartnerNotFoundErrorResponsePayload,
  IResponseErrorPayload,
} from 'onboarding/types/response'
import {
  throwOnAggregateWorkflowError,
  throwOnErrorWithoutPayload,
  throwOnExpiredError,
  throwOnInvalidChangePartnerError,
  throwOnInvalidRequestParams,
  throwOnPartnerNotFoundError,
} from 'onboarding/utils/errorResponse/throwOnApiError'

type TFetchRequestDetailsParams = {
  id: number
  type: RequestTypes
  isCached?: boolean
}

export enum RequestCrudKey {
  ContractRequestList = 'ContractRequestList',
}

const CONTROLLER = 'request'

export type TCreateAndStartRequestResult = {
  requestId: number
  requestUid: string
}

type TCreateAndStartResponseData = {
  requestId: number
  requestUid: string
  errorPayload?:
    | IInvalidParamsErrorResponsePayload
    | IAggregateWorkflowErrorResponsePayload
    | IResponseErrorPayload
}

class OnboardingRequestApi {
  public async fetchRequestDetail({ id, type, isCached }: TFetchRequestDetailsParams) {
    let crudKey

    switch (type) {
      case RequestTypes.SimplePartnerRegistration:
        crudKey = CrudStoreItemOnboarding.SimplePartnerRegistrationRequests
        break
      case RequestTypes.BPRegistration:
        crudKey = CrudStoreItemOnboarding.BPRegistrationRequestList
        break
      case RequestTypes.BPReconfirmation:
        crudKey = CrudStoreItemOnboarding.BPReconfirmationRequestList
        break
      default:
        throw new Error('Unsupported request type')
    }

    const source = CrudStore.get(crudKey)
    const result = await source.getById({ id, force: !isCached, keyField: 'EntryId' })

    return result || null
  }

  public getRequestListCrud(cacheKey: string) {
    return CrudStore.get<IRequest>(CrudStoreItemOnboarding.RequestList, cacheKey)
  }

  public getContractRequestListCrud(crudKey?: string): Crud<IRequest, number | string> {
    return CrudStore.get<IRequest>(CrudStoreItemOnboarding.ContractRequestList, crudKey)
  }

  public async updateCaseOwnerInRequestListForm(requestId: number, caseOwner: string) {
    const source = CrudStore.get(CrudStoreItemOnboarding.RequestList)
    return source.update({ formId: '661', code: 'RequestListForm' }, [
      { EntryId: requestId, CaseOwner: caseOwner },
    ])
  }

  /**
   * @throws {ValidationParamsError | InvalidChangePartnerError | PartnerNotFoundError | Error}
   * @param requestId
   * @param partnerId
   */
  public async changePartner(requestId: number, partnerId: string): Promise<boolean> {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<IChangePartnerResponse>({
      service: CONTROLLER,
      action: 'changePartner',
      payload: { requestId, partnerId },
      withoutTokenRefresh: true,
      returnError: true,
      isAuth: true,
    })
    if (response.status !== 'success') {
      const fallbackMessage = `Failed to change partner to partnerId=${partnerId} in requestId=${requestId}`
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnInvalidChangePartnerError(response, fallbackMessage)
      throwOnPartnerNotFoundError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return response.data.changed
  }

  /**
   * @throws {ValidationParamsError | InvalidChangePartnerError | Error}
   * @param requestId
   */
  public async canChangePartner(requestId: number): Promise<true> {
    const wsClient = WebSocketStore.get('onboarding')

    const response = await wsClient.call<ICanChangePartnerResponse>({
      service: CONTROLLER,
      action: 'canChangePartner',
      payload: { requestId },
      withoutTokenRefresh: true,
      returnError: true,
      isAuth: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to check if partner can be changed'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnInvalidChangePartnerError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return true
  }

  /**
   * @throws {ValidationParamsError | InvalidChangePartnerError | PartnerNotFoundError | Error}
   * @param requestId
   * @param partnerParams
   */
  public async canChangePartnerTarget(
    requestId: number,
    partnerParams: TFetchPartnerDataParams
  ): Promise<true> {
    const wsClient = WebSocketStore.get('onboarding')

    const response = await wsClient.call<ICanChangePartnerResponse>({
      service: CONTROLLER,
      action: 'canChangePartnerTarget',
      payload: { requestId, partnerParams },
      withoutTokenRefresh: true,
      returnError: true,
      isAuth: true,
    })

    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to check if partner is valid'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnInvalidChangePartnerError(response, fallbackMessage)
      throwOnPartnerNotFoundError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return true
  }

  public getLastNChangesOfPartnerInRequest = async (
    requestId: number,
    limit: number
  ): Promise<TResponse<ILastNChangesOfPartnerResponse>> => {
    const wsClient = WebSocketStore.get('onboarding')
    let result

    try {
      const payload = { requestId, limit }
      result = await wsClient.call<ILastNChangesOfPartnerResponse>({
        service: CONTROLLER,
        action: 'getLastNChangesOfPartner',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      const errorMessage = `Failed to get last ${limit} changes of partner in request id=${requestId}`
      result = getErrorResponse<ILastNChangesOfPartnerResponse>(e.message || errorMessage, {
        lastChanges: [],
      })
    }

    return result
  }

  public async fetchOnePendingByPartnerAndType(partnerId: string, requestType: RequestTypes) {
    const wsClient = WebSocketStore.get('onboarding')
    let result

    try {
      const payload = { partnerId, requestType }
      result = await wsClient.call<{
        pendingRequest: IRequestWithSpecializationName | null
      }>({
        service: CONTROLLER,
        action: 'getPendingRequestForPartner',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      const errorMessage = `Error in getPendingRequestForPartner for partnerId=${partnerId}`
      result = getErrorResponse(e.message || errorMessage, {
        pendingRequest: null,
      })
    }

    return result
  }

  public async reopenOne(requestId: number): Promise<true> {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{
      errorPayload?: IInvalidParamsErrorResponsePayload | IExpiredErrorResponsePayload | undefined
    }>({
      service: CONTROLLER,
      action: 'reopen',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to reopen request'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnExpiredError(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return true
  }

  public async removeOne(requestId: number) {
    const wsClient = WebSocketStore.get('onboarding')
    try {
      const res = await wsClient.call({
        service: CONTROLLER,
        action: 'removeRequest',
        payload: { RequestId: requestId },
        withoutTokenRefresh: true,
        isAuth: true,
      })
      return {
        success: true,
        message: res.data.message,
      }
    } catch (e: any) {
      return { success: false, message: e }
    }
  }

  public async reviveOne(requestId: number): Promise<true> {
    const wsClient = WebSocketStore.get('onboarding')
    const response = await wsClient.call<{ errorPayload?: IInvalidParamsErrorResponsePayload }>({
      service: CONTROLLER,
      action: 'revive',
      payload: {
        requestId,
      },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const fallbackMessage = 'Failed to revive request'
      throwOnInvalidRequestParams(response, fallbackMessage)
      throwOnErrorWithoutPayload(response, fallbackMessage)
    }

    return true
  }

  private async startRequest(payload: IObjectAny) {
    const response = await WebSocketStore.get(
      WebSocketStoreKeys.Onboarding
    ).call<TCreateAndStartResponseData>({
      service: CONTROLLER,
      action: 'startRequest',
      payload,
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const defaultMessage = 'Failed to create request'
      throwOnInvalidRequestParams(response)
      throwOnAggregateWorkflowError(response, defaultMessage)
      throwOnErrorWithoutPayload(response, defaultMessage)
    }

    return response.data
  }

  public async createAndStartOne(payload: IObjectAny) {
    const response = await this.startRequest(payload)

    return response.requestId
  }

  public async createAndStartOneWithUid(
    payload: IObjectAny
  ): Promise<TCreateAndStartRequestResult> {
    const response = await this.startRequest(payload)

    return {
      requestId: response.requestId,
      requestUid: response.requestUid,
    }
  }
}

export const onboardingRequestApi = new OnboardingRequestApi()

interface ILastNChangesOfPartnerResponse {
  lastChanges: ILastPartnerChange[]
}

interface ICanChangePartnerResponse {
  canChange: true
  errorPayload?:
    | IInvalidParamsErrorResponsePayload
    | IInvalidChangePartnerErrorResponsePayload
    | IPartnerNotFoundErrorResponsePayload
}

interface IChangePartnerResponse {
  changed: boolean
  errorPayload: ICanChangePartnerResponse['errorPayload']
}

export type TFetchPartnerDataParams = { partnerId: string } | { locationId: string }
